/**
 * W67 Phase 8 Variante B (2026-05-25) — Demo-Mode-Context.
 *
 * Wraps Dashboard/Watchlist/SymbolDetail/Strategies in einer Demo-Variante
 * die keine Mutations erlaubt. Statt 4 separate Demo-Pages zu pflegen,
 * nutzt die Demo-Route ab jetzt die echten Components mit einem
 * DemoModeProvider drumherum.
 *
 * Verwendung:
 *
 *   <DemoModeProvider>
 *     <Dashboard />
 *   </DemoModeProvider>
 *
 * In den Mutation-Handlern:
 *
 *   const { isDemoMode, blockMutation } = useDemoMode();
 *   const onClick = () => {
 *     if (isDemoMode) return blockMutation('Trade-Plan speichern');
 *     // ... echte Mutation
 *   };
 *
 * Beim Block wird ein Toast gezeigt "Demo-Modus: $action nicht moeglich".
 */

import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

interface DemoModeContextValue {
  /** True wenn wir gerade die Demo-Variante rendern. */
  isDemoMode: boolean;
  /**
   * Block eine Mutation und zeige ein Toast/Alert.
   * Returns immer false damit Handler `if (blockMutation(...)) return` schreiben koennen.
   */
  blockMutation: (action: string) => false;
  /**
   * Path-Prefix fuer alle Navigations innerhalb der Demo. In normaler App ''.
   * In Demo '/demo'. Damit funktionieren <Link>/navigate(linkTo(path)).
   */
  linkTo: (path: string) => string;
}

const DemoModeContext = createContext<DemoModeContextValue>({
  isDemoMode: false,
  blockMutation: () => false,
  linkTo: (path) => path,
});

interface ProviderProps {
  children: ReactNode;
  /** Override fuer Tests. Defaults to true wenn Component direkt gemounted wird. */
  enabled?: boolean;
}

export function DemoModeProvider({ children, enabled = true }: ProviderProps) {
  const blockMutation = useCallback((action: string): false => {
    // Schlanke Browser-Notification — kein Toast-Lib-Dep.
    // In Production wuerde man hier einen Sonner/Radix-Toast triggern.
    // Hier bewusst alert() weil das im Demo-Kontext explizit + clickbar ist.
    if (typeof window !== 'undefined') {
      alert(
        `Demo-Modus aktiv\n\n"${action}" ist hier nicht moeglich.\n\nDas ist eine Read-Only-Vorschau der App. Registriere dich um eigene Trades zu planen.`
      );
    }
    return false;
  }, []);

  const linkTo = useCallback(
    (path: string): string => {
      if (!enabled) return path;
      // Stelle sicher dass path mit / startet
      const normalized = path.startsWith('/') ? path : `/${path}`;
      // Vermeide /demo/demo-Verkettung
      if (normalized.startsWith('/demo')) return normalized;
      return `/demo${normalized}`;
    },
    [enabled]
  );

  const value = useMemo<DemoModeContextValue>(
    () => ({
      isDemoMode: enabled,
      blockMutation,
      linkTo,
    }),
    [enabled, blockMutation, linkTo]
  );

  return (
    <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>
  );
}

/**
 * Hook zum Lesen des Demo-Modus.
 *
 * Wenn nicht in einem DemoModeProvider gemounted (z.B. normale App):
 * isDemoMode = false, blockMutation ist no-op.
 */
export function useDemoMode(): DemoModeContextValue {
  return useContext(DemoModeContext);
}
